import { Link } from "react-router-dom";
import { CheckCircle, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { AnimatedDiv } from "@/components/motion";
import { networkRegions, corridor, capabilities } from "./data";

export const DomesticNetwork = () => (
  <section className="py-24 lg:py-32 bg-white" data-testid="domestic-network">
    <div className="max-w-7xl mx-auto px-6 lg:px-8">
      <AnimatedDiv className="max-w-2xl mb-16">
        <p className="text-xs font-sans uppercase tracking-[0.2em] text-[#C5A059] mb-4">Domestic Network</p>
        <h2 className="text-3xl md:text-4xl font-serif tracking-tight text-foreground mb-4">Throughout India</h2>
        <p className="text-base font-sans text-muted-foreground">
          A distribution footprint organised across the country's key textile-consuming regions.
        </p>
      </AnimatedDiv>
      <div className="grid md:grid-cols-3 gap-8">
        {networkRegions.map((r) => (
          <AnimatedDiv key={r.zone} className="border border-border p-8 bg-background">
            <h3 className="font-serif text-xl text-foreground mb-6 pb-4 border-b border-border">{r.zone}</h3>
            <ul className="space-y-4">
              {r.places.map((p) => (
                <li key={p} className="flex items-center gap-3">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#C5A059] flex-shrink-0" />
                  <span className="text-sm font-sans text-foreground">{p}</span>
                </li>
              ))}
            </ul>
          </AnimatedDiv>
        ))}
      </div>
    </div>
  </section>
);

export const InternationalCorridor = () => (
  <section className="py-24 lg:py-32 bg-[#0F172A]" data-testid="international-corridor">
    <div className="max-w-7xl mx-auto px-6 lg:px-8">
      <AnimatedDiv className="max-w-2xl mb-20">
        <p className="text-xs font-sans uppercase tracking-[0.2em] text-[#C5A059] mb-4">Beyond Borders</p>
        <h2 className="text-3xl md:text-5xl font-serif tracking-tight text-white mb-4">From Surat to the Gulf</h2>
        <p className="text-base font-sans text-white/60 leading-relaxed">
          A global vision built on long-standing partnerships — our fabrics travel a proven corridor from India's textile capital to the Middle East.
        </p>
      </AnimatedDiv>
      <AnimatedDiv>
        <div className="grid md:grid-cols-4 gap-10 md:gap-0 relative">
          <div className="hidden md:block absolute top-[7px] left-[12.5%] right-[12.5%] h-px bg-gradient-to-r from-[#C5A059]/30 via-[#C5A059] to-[#C5A059]/30" />
          {corridor.map((c, i) => (
            <div key={c.city} className="relative md:text-center md:px-4">
              <div className="flex md:justify-center items-center gap-4 md:gap-0 mb-4">
                <span className="relative z-10 w-[15px] h-[15px] rounded-full border-2 border-[#C5A059] bg-[#0F172A] flex-shrink-0">
                  {i === 0 && <span className="absolute inset-[2px] rounded-full bg-[#C5A059]" />}
                </span>
              </div>
              <p className="font-serif text-2xl text-white">{c.city}</p>
              <p className="text-xs font-sans uppercase tracking-[0.2em] text-[#C5A059] mt-2">{c.role}</p>
            </div>
          ))}
        </div>
      </AnimatedDiv>
    </div>
  </section>
);

export const ExportCapabilities = () => (
  <section className="py-24 lg:py-32 bg-white">
    <div className="max-w-7xl mx-auto px-6 lg:px-8">
      <AnimatedDiv className="max-w-2xl mb-12">
        <h2 className="text-3xl md:text-4xl font-serif tracking-tight text-foreground mb-4">Export Capabilities</h2>
        <p className="text-base font-sans text-muted-foreground">What makes us a reliable partner — at home and abroad.</p>
      </AnimatedDiv>
      <div className="grid md:grid-cols-2 gap-6">
        {capabilities.map((cap) => (
          <AnimatedDiv key={cap} className="flex items-center gap-4 p-6 border border-border/50 bg-background">
            <CheckCircle size={18} className="text-[#C5A059] flex-shrink-0" />
            <span className="text-sm font-sans text-foreground">{cap}</span>
          </AnimatedDiv>
        ))}
      </div>
    </div>
  </section>
);

export const PresenceCTA = () => (
  <section className="py-24 bg-[#0F172A]">
    <div className="max-w-7xl mx-auto px-6 lg:px-8 text-center">
      <h2 className="text-3xl md:text-4xl font-serif text-white mb-6">Ready to partner with us?</h2>
      <Link to="/contact">
        <Button data-testid="presence-cta-btn" className="bg-[#C5A059] text-[#0F172A] rounded-none px-8 py-6 text-xs uppercase tracking-[0.15em] font-sans font-medium hover:bg-[#C5A059]/90">
          Get in Touch <ArrowRight size={14} className="ml-2" />
        </Button>
      </Link>
    </div>
  </section>
);
